import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { QRCodeSVG } from 'qrcode.react';
import { publicApi } from '../../lib/api';
import { CheckCircle2, Clock, XCircle, Loader2, MapPin, Calendar, Wallet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import './nazakat-shararat.css';

const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

export default function NazakatTicketPage() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  // ── Domain bridge — redirect preview / non-canonical hosts to production ──
  // The Nazakat WhatsApp confirmation URL uses the Meta-approved template
  // whose URL button is baked to the preview domain (see PublicTicketPage.js
  // for the full rationale). Until the template is re-approved with the
  // canonical scratchjsr.com domain, mirror the same bridge redirect here so
  // deep-links to /events/specialevents/nazakat-shararat/ticket/:token from
  // the preview host also land on production. `?stay=1` bypasses for testing.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname;
    const isCanonical = host === 'scratchjsr.com' || host === 'www.scratchjsr.com';
    const isLocal = host === 'localhost' || host.startsWith('127.') || host.endsWith('.local');
    const bypass = new URLSearchParams(window.location.search).get('stay') === '1';
    if (!isCanonical && !isLocal && !bypass && token) {
      const target = `https://scratchjsr.com${window.location.pathname}${window.location.search || ''}`;
      window.location.replace(target);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await publicApi.sxeGetBookingByToken(token);
        if (cancelled) return;
        setB(res.data);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Ticket not found.');
      }
    };
    load();
    const poll = setInterval(() => setTick((t) => t + 1), 3000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [token]);

  useEffect(() => {
    if (b && b.payment_status === 'initiated' && tick > 0 && tick < 20) {
      publicApi.sxeGetBookingByToken(token).then((res) => setB(res.data)).catch(() => {});
    }
  }, [tick, b, token]);

  // ── QR payload ────────────────────────────────────────────────────────
  // Encode ONLY the compact `qr_token` (URL-safe, ~30 chars). A tiny payload
  // → fewer modules → larger visual squares → far more reliable at the gate,
  // especially on printed passes or under low light. The backend scanner
  // endpoint resolves the token to the full booking with balance/status.
  const qrPayload = b?.qr_token || '';

  if (err) {
    return (
      <div className="naz-page min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <XCircle size={40} className="text-red-400 mx-auto mb-3" />
          <p className="text-[#F2F2F2] mb-4">{err}</p>
          <Link to="/events/specialevents/nazakat-shararat">
            <Button className="naz-btn-gold">Back to Event</Button>
          </Link>
        </div>
      </div>
    );
  }
  if (!b) {
    return (
      <div className="naz-page min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4A843] animate-spin" />
      </div>
    );
  }

  const isPaid    = b.payment_status === 'paid';
  const isPending = b.payment_status === 'initiated' || b.payment_status === 'pending';
  const isFailed  = b.payment_status === 'failed' || b.payment_status === 'cancelled';

  return (
    <div className="naz-page min-h-screen py-8 px-4">
      <Helmet>
        {/* Private tickets — must NOT be indexed by search engines */}
        <title>Your Pass · Nazakat &amp; Shararat</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <div className="max-w-md mx-auto">
        <div className="text-center mb-4">
          <p className="naz-eyebrow">Nazakat &amp; Shararat</p>
          <h1 className="naz-serif text-3xl text-[#F1E2B8] mt-1">Your Reservation</h1>
        </div>

        <div className="naz-modal rounded-xl p-5" data-testid="naz-ticket-card">
          {isPending && (
            <div className="text-center py-4">
              <Clock size={28} className="text-[#D4A843] mx-auto mb-2 animate-pulse" />
              <p className="text-[#F1E2B8] font-medium">Verifying payment</p>
              <p className="text-[#F2F2F2]/60 text-xs mt-1">The bank is confirming your transaction.</p>
            </div>
          )}

          {isFailed && (
            <div className="text-center py-4">
              <XCircle size={28} className="text-red-400 mx-auto mb-2" />
              <p className="text-[#F1E2B8] font-medium">Payment did not go through</p>
              <p className="text-[#F2F2F2]/60 text-xs mt-1">No amount was captured. Please try again.</p>
              <Link to="/events/specialevents/nazakat-shararat" className="inline-block mt-4">
                <Button className="naz-btn-gold">Try Again</Button>
              </Link>
            </div>
          )}

          {isPaid && (
            <>
              <div className="flex items-center justify-center gap-2 mb-4">
                <CheckCircle2 size={16} className="text-emerald-400" />
                <span className="text-emerald-300 text-xs uppercase tracking-widest">Confirmed</span>
              </div>

              <div className="naz-qr-frame" data-testid="naz-ticket-qr-frame">
                <QRCodeSVG
                  value={qrPayload}
                  size={288}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#0A0A0A"
                  marginSize={3}
                  data-testid="naz-ticket-qr"
                />
              </div>

              {b.pay_at_gate > 0 && (
                <div className="rounded-lg p-3 border border-amber-400/40 bg-amber-400/8 mb-3 text-center"
                     data-testid="naz-balance-due-panel">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5">
                    <Wallet size={12} className="inline mr-1" /> Balance due at gate
                  </p>
                  <p className="naz-serif text-3xl text-[#F1E2B8]" data-testid="naz-balance-due-amount">
                    {fmtINR(b.pay_at_gate)}
                  </p>
                  <p className="text-xs text-amber-100/70 mt-0.5">Payable on arrival — Full Cover.</p>
                </div>
              )}

              <div className="text-sm text-[#F2F2F2]/85 space-y-1.5">
                <div className="flex justify-between border-b border-[#D4A843]/10 pb-1">
                  <span className="text-[#F2F2F2]/55">Name</span>
                  <span>{b.customer_name}</span>
                </div>
                <div className="flex justify-between border-b border-[#D4A843]/10 pb-1">
                  <span className="text-[#F2F2F2]/55">Order</span>
                  <span className="font-mono text-[#D4A843] text-xs">{b.order_id}</span>
                </div>
                <div className="flex justify-between border-b border-[#D4A843]/10 pb-1">
                  <span className="text-[#F2F2F2]/55"><Calendar size={11} className="inline mr-1" />Date</span>
                  <span>Sat 18 July · 8PM+</span>
                </div>
                <div className="flex justify-between border-b border-[#D4A843]/10 pb-1">
                  <span className="text-[#F2F2F2]/55"><MapPin size={11} className="inline mr-1" />Venue</span>
                  <span className="text-right text-xs">{b.event.venue}</span>
                </div>
              </div>

              <div className="mt-3 pt-2 border-t border-[#D4A843]/15">
                <p className="text-[10px] uppercase tracking-widest text-[#F2F2F2]/50 mb-1.5">Items</p>
                {b.items.map((it) => (
                  <div key={it.sku_id} className="flex justify-between text-xs text-[#F2F2F2]/80 py-0.5">
                    <span>{it.sku_id} × {it.qty}</span>
                    <span className="tabular-nums">{fmtINR(it.line_total)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm text-[#F1E2B8] font-medium mt-2 pt-1.5 border-t border-[#D4A843]/15">
                  <span>Paid now</span>
                  <span className="tabular-nums">{fmtINR(b.pay_now)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
